// Setting up two-factor authentication: QR code (or the key typed in by hand) for the authenticator
// app, then the first code to confirm it. Resolves the one-time recovery codes.
import { useEffect, useRef, useState } from 'react';
import type { TotpSetupResponse } from '../../../shared/types';
import { totpEnable, totpSetup } from '../lib/account';
import { copyText } from '../export';
import { accountErrorText, CodeInput, codeLooksComplete, FormError } from './AccountForms';
import { Icon } from './Icon';
import { toast } from './Toasts';

/** "JBSW Y3DP EHPK 3PXP" – the base32 key in groups of four, easier to type. */
export function groupSecret(secret: string): string {
  return (secret.replace(/\s+/g, '').toUpperCase().match(/.{1,4}/g) ?? []).join(' ');
}

/**
 * The server's QR code (an SVG document) as an image URL. Rendered through <img>, never inserted
 * into the page: an SVG shown as an image cannot run scripts or load anything.
 */
export function svgDataUrl(svg: string): string | null {
  const text = svg.trim();
  if (!/^(<\?xml[^>]*>\s*)?<svg[\s>]/i.test(text)) return null;
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return `data:image/svg+xml;base64,${btoa(binary)}`;
}

interface TotpSetupFormProps {
  /** Called with the recovery codes once the code was confirmed (2FA is on from now). */
  onEnabled: (codes: string[]) => void;
  onCancel?: () => void;
}

export function TotpSetupForm({ onEnabled, onCancel }: TotpSetupFormProps) {
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  // Every setup call replaces the pending secret: ask once per mount (also under StrictMode).
  const started = useRef<Promise<TotpSetupResponse> | null>(null);

  const start = (force = false) => {
    if (started.current && !force) return started.current;
    setLoadError(null);
    setSetup(null);
    const p = totpSetup();
    started.current = p;
    p.then(
      (res) => {
        if (started.current === p) setSetup(res);
      },
      (err: unknown) => {
        if (started.current === p) setLoadError(accountErrorText(err, 'Nastavitve ni bilo mogoče začeti.'));
      },
    );
    return p;
  };

  useEffect(() => {
    void start();
  }, []);

  const submit = async () => {
    if (!setup || busy || !codeLooksComplete(code)) return;
    setBusy(true);
    setError(null);
    try {
      const codes = await totpEnable(code);
      onEnabled(codes);
    } catch (err) {
      setError(accountErrorText(err, 'Dvostopenjske prijave ni bilo mogoče vklopiti.'));
      codeRef.current?.select();
    } finally {
      setBusy(false);
    }
  };

  const qr = setup ? svgDataUrl(setup.qrSvg) : null;
  const deepLink = setup && /^otpauth:\/\//i.test(setup.otpauthUrl) ? setup.otpauthUrl : null;

  return (
    <form
      className="stack totp-setup"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <ol className="steps">
        <li>
          Na telefon namestite aplikacijo za preverjanje, npr. <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong>{' '}
          ali <strong>2FAS</strong>.
        </li>
        <li>V njej dodajte račun: skenirajte kodo QR ali vpišite ključ.</li>
        <li>Vpišite 6-mestno kodo, ki jo prikaže aplikacija.</li>
      </ol>

      {loadError ? (
        <div className="notice notice--error" role="alert">
          <Icon name="alert" size={20} className="notice__icon" />
          <div className="notice__content">
            <p>{loadError}</p>
            <button type="button" className="btn btn--secondary btn--small" onClick={() => void start(true)}>
              <Icon name="refresh" size={18} /> Poskusi znova
            </button>
          </div>
        </div>
      ) : !setup ? (
        <p className="muted loading-line">
          <Icon name="spinner" size={18} /> Pripravljam kodo QR …
        </p>
      ) : (
        <>
          <div className="totp-qr">
            {qr ? (
              <img src={qr} alt="Koda QR za aplikacijo za preverjanje" width={200} height={200} />
            ) : (
              <p className="muted small">Kode QR ni mogoče prikazati – vpišite ključ ročno.</p>
            )}
          </div>
          {deepLink && (
            <a className="btn btn--secondary btn--block" href={deepLink}>
              <Icon name="lock" size={20} /> Dodaj v aplikacijo na tem telefonu
            </a>
          )}
          <div className="totp-key">
            <span className="muted small">Ali v aplikacijo vpišite ključ (časovno osnovan, 6 števk):</span>
            <code className="totp-secret" data-secret={setup.secret}>
              {groupSecret(setup.secret)}
            </code>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() =>
                void copyText(setup.secret).then((ok) =>
                  toast(ok ? 'Ključ je kopiran v odložišče.' : 'Brskalnik ni dovolil kopiranja v odložišče.', { kind: ok ? 'success' : 'warning' }),
                )
              }
            >
              <Icon name="copy" size={18} /> Kopiraj ključ
            </button>
          </div>
          <CodeInput
            id="totp-confirm"
            value={code}
            onChange={setCode}
            inputRef={codeRef}
            invalid={error !== null}
            describedBy={error ? 'totp-error' : undefined}
            disabled={busy}
          />
          {error && <FormError id="totp-error">{error}</FormError>}
          <div className="form-actions">
            {onCancel && (
              <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={busy}>
                Prekliči
              </button>
            )}
            <button type="submit" className="btn btn--primary" disabled={busy || !codeLooksComplete(code)}>
              <Icon name={busy ? 'spinner' : 'check'} size={20} /> Vklopi
            </button>
          </div>
        </>
      )}
    </form>
  );
}
